<template>
  <div class="min-w-[100px] max-w-80 items-start">
    <!-- Si hay 2 o menos elementos: columna única con ancho ajustado al contenido -->
    <div v-if="items.length <= 2" class="flex flex-col gap-1.5 w-fit">
      <div
        v-for="item in items"
        :key="item.name"
        :class="[
          'inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium',
          item.tipo === 'tipoAtencion' ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' : getStatusStyle(item)
        ]">
        <component
          :is="getIcon(item)"
          v-if="['tipoAtencion','estadoAsistencial','estadoServicio','tipoOrden','contrato','empresa', 'referencia', 'rda'].includes(item.tipo)"
          class="size-3.5 shrink-0" />
        <span class="truncate">{{ validateName(item.name) }}</span>
      </div>
    </div>

    <!-- Si hay más de 2 elementos: dos columnas con ancho controlado -->
    <div v-else class="grid grid-cols-2 gap-x-2 gap-y-1.5 w-full">
      <div
        v-for="item in items"
        :key="item.name"
        :class="[
          'inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium',
          item.fullWidth ? 'col-span-2' : 'whitespace-nowrap',
          getStatusStyle(item)
        ]">
        <component
          :is="getIcon(item)"
          v-if="['tipoAtencion','estadoAsistencial','estadoServicio','tipoOrden','contrato','empresa', 'referencia', 'rda'].includes(item.tipo)"
          class="size-3.5 shrink-0" />
        <span class="truncate">{{ validateName(item.name) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { listEstados } from '@/modules/Core/usings/constantes'
import { colorEstados } from '@/modules/Core/usings/generales'
import {
  Squares2X2Icon,
  BeakerIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  BoltIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  IdentificationIcon,
  PlayCircleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  TicketIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  DocumentPlusIcon,
  HeartIcon,
  GlobeAltIcon,
  NewspaperIcon,
  UserPlusIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps<listEstados>()

// Mapa de estilos para cada tipo de estado
const styleMap = {
  estadoAsistencial: {
    // Estado inicial - Azul suave
    'AGENDADO': 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-700/10 dark:bg-sky-400/10 dark:text-sky-400 dark:ring-sky-400/20',

    // Estado en progreso - Amarillo energético
    'ADMISIONADO': 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-700/10 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/20',

    // Estado activo - Verde vibrante
    'ABIERTO': 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-700/10 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-emerald-400/20',

    // Estado cancelado - Rojo intenso
    'CANCELADO': 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-700/10 dark:bg-rose-400/10 dark:text-rose-400 dark:ring-rose-400/20',

    // Estado completado - Verde exitoso
    'FINALIZADO': 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-900/10 dark:bg-green-700/10 dark:text-green-700 dark:ring-green-700/20',

    // Estado reprogramado - Púrpura informativo
    'REAGENDADO': 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-700/10 dark:bg-violet-400/10 dark:text-violet-400 dark:ring-violet-400/20',

    // Estado Tramitado - Azul suave
    'TRAMITADO': 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-700/10 dark:bg-sky-400/10 dark:text-sky-400 dark:ring-sky-400/20',
  },
  estadoServicio: {
    // Estado no realizado - Rojo intenso
    'NO REALIZADO': 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-700/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20',

    // Estado en progreso - Amarillo energético
    'ABIERTO': 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-700/10 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/20',

    // Estado aprobado - Verde éxito
    'TRAMITADO': 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-900/10 dark:bg-green-700/10 dark:text-green-700 dark:ring-green-700/20',

    // Estado cancelado - Gris neutro
    'CANCELADO': 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-700/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20',
  },
  tipoOrden: {
    // Remisiones - Verde
    'REMISIONES': 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-900/10 dark:bg-green-700/10 dark:text-green-700 dark:ring-green-700/20',

    // Procedimientos - Azul
    'PROCEDIMIENTOS': 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/20',

    // Laboratorios - Púrpura
    'LABORATORIOS': 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-700/10 dark:bg-purple-400/10 dark:text-purple-400 dark:ring-purple-400/20',

    // Dispensarios - Amarillo
    'DISPENSARIOS': 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-700/10 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/20',

    'RECETARIOS': 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-700/10 dark:bg-amber-400/10 dark:text-amber-400 dark:ring-amber-400/20',

    'ORDEN EXTERNA': 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-700/10 dark:bg-orange-400/10 dark:text-orange-400 dark:ring-orange-400/20',
    'ORDEN INTERNA': 'bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-700/10 dark:bg-teal-400/10 dark:text-teal-400 dark:ring-teal-400/20',
    'OTROS SERVICIOS': 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-700/10 dark:bg-rose-400/10 dark:text-rose-400 dark:ring-rose-400/20',
    'DEMANDA INDUCIDA': 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-700/10 dark:bg-emerald-400/10 dark:text-emerald-400 dark:ring-emerald-400/20',
  },
  tipoAtencion: {
    // Remisiones - Verde
    'CONSULTA': 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-900/10 dark:bg-green-700/10 dark:text-green-700 dark:ring-green-700/20',

    // Procedimientos - Azul
    'PROCEDIMIENTO': 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-900/10 dark:bg-blue-700/10 dark:text-blue-700 dark:ring-blue-700/20',

    // Laboratorios - Púrpura
    'LABORATORIO': 'bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-900/10 dark:bg-purple-700/10 dark:text-purple-700 dark:ring-purple-700/20',

    // Orden Externa - Naranja
    'VACUNA': 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-900/10 dark:bg-orange-700/10 dark:text-orange-700 dark:ring-orange-700/20',

    // Orden Externa - Rosado
    'OTROS SERVICIOS': 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-700/10 dark:bg-rose-400/10 dark:text-rose-400 dark:ring-rose-400/20',
  },
  contrato: {
    // Estilo neutro informativo - Azul intenso
    'default': 'bg-gray-200 text-gray-700 ring-1 ring-inset ring-gray-700/10 dark:bg-gray-400/10 dark:text-gray-300 dark:ring-gray-300/20'
  },
  empresa: {
    'default': 'font-bold bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-700/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20'
  },
  referencia: {
    'default': 'font-bold bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-800/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/20'
  },
  rda: {
    'RDA ENVIADO': 'font-bold bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-800/10 dark:bg-blue-400/10 dark:text-blue-400 dark:ring-blue-400/20',
    'RDA NO ENVIADO': 'font-bold bg-gray-50 text-gray-800 ring-1 ring-inset ring-gray-800/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20',
    'default': 'font-bold bg-gray-50 text-gray-800 ring-1 ring-inset ring-gray-800/10 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-gray-400/20'
  },
  estadoRecetario: {
    'RECETADO': 'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-700/10 dark:bg-cyan-400/10 dark:text-cyan-400 dark:ring-cyan-400/20',
    'EN PROCESO': 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-900/10 dark:bg-amber-700/10 dark:text-amber-700 dark:ring-amber-700/20',
    'DISPENSADO': 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-700/10 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-400/20'
  }
}

const iconMap = {
  tipoAtencion: {
    PROCEDIMIENTO: Squares2X2Icon,
    LABORATORIO: BeakerIcon,
    CONSULTA: UserGroupIcon,
    HOSPITALIZACION: BuildingOffice2Icon,
    URGENCIA: BoltIcon,
    TRIAGE: ClipboardDocumentCheckIcon,
    VACUNA: TicketIcon,
    'OTROS SERVICIOS': NewspaperIcon
  },
  estadoAsistencial: {
    AGENDADO: ClockIcon,
    ADMISIONADO: IdentificationIcon,
    'ABIERTO': PlayCircleIcon,
    CANCELADO: XCircleIcon,
    FINALIZADO: CheckCircleIcon,
    REAGENDADO: ArrowPathIcon,
    'TRAMITADO CON ANEXO': CheckCircleIcon,
    INASISTIDO: XCircleIcon
  },
  estadoServicio:{
    'NO REALIZADO': ClockIcon,
    'ABIERTO': PlayCircleIcon,
    CANCELADO: XCircleIcon,
    TRAMITADO: IdentificationIcon,
  },
  tipoOrden: {
    REMISIONES: DocumentTextIcon,
    PROCEDIMIENTOS: Cog6ToothIcon,
    LABORATORIOS: BeakerIcon,
    DISPENSARIOS: HeartIcon,
    RECETARIOS: HeartIcon,
    'ORDEN EXTERNA': DocumentPlusIcon,
    'ORDEN INTERNA': DocumentPlusIcon,
    'OTROS SERVICIOS': NewspaperIcon,
    'DEMANDA INDUCIDA': UserPlusIcon,
  },
  contrato: {
    'default': DocumentTextIcon
  },
  empresa: {
    'default': BuildingOffice2Icon
  },
  referencia: {
    'default': DocumentTextIcon
  },
  rda: {
    'default': GlobeAltIcon
  }
}
const getStatusStyle = (item: unknown) => {
  const tipo = (item as object)['tipo'] as string
  if (tipo === 'estadoAsistencial') {
    return colorEstados[(item as object)['name'] as keyof typeof colorEstados] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }
  const map = styleMap[tipo as keyof typeof styleMap]
  if (tipo === 'contrato' || tipo === 'empresa' || tipo === 'referencia') {
    return map?.['default' as keyof typeof map] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 min-w-full'
  }
  return map?.[(item as object)['name'] as keyof typeof map] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
}
const getIcon = (item: unknown) => {
  const map = iconMap[(item as object)['tipo']as keyof typeof iconMap]
  if ((item as object)['tipo'] === 'contrato' || (item as object)['tipo'] === 'empresa' || (item as object)['tipo'] === 'referencia' || (item as object)['tipo'] === 'rda') {
    return map?.['default' as keyof typeof map]
  }
  return map?.[(item as object)['name']as keyof typeof map]
}
const validateName = (name: string) => {
  if (!props.dispensario) return name
  const tiposAdmisiones = ['CONSULTA', 'DISPENSACION', 'DISPENSARIO ', 'ENTREGA EXTERNA', 'EXTERNA', 'LABORATORIO', 'PROCEDIMIENTO']
  return name === 'ENTREGA EXTERNA' ? 'ENTREGA EXTERNA' : tiposAdmisiones.includes(name) ? 'ENTREGA INTERNA' : name
}
</script>
